import React, { useCallback, useMemo, useState } from 'react';
import { Spin } from 'antd';
import Drawer from '../../../components/AppDrawer';
import LeadForm from './LeadForm';
import ContactFormDrawer from '../../contacts/components/ContactFormDrawer';
import { useCreateContact } from '../../contacts/hooks/useCreateContact';
import { useUserOptions } from '../../../hooks/options/useUserOptions';
import { useContactCategories } from '../../contacts/hooks/useContactCategories';
import { useContactRoles } from '../../contacts/hooks/useContactRoles';
import { useLead } from '../hooks/useLead';
import { useLeadPipeline } from '../hooks/useLeadPipeline';
import { useLeadSources } from '../hooks/useLeadSources';
import type { ContactFormValues } from '../../contacts/types';
import type { LeadFormValues } from '../types';

export interface LeadFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editLeadId?: string | null;
}

const LeadFormDrawer: React.FC<LeadFormDrawerProps> = ({ open, onClose, onSuccess, editLeadId }) => {
  const [quickAddContactOpen, setQuickAddContactOpen] = useState(false);
  const [newContactId, setNewContactId] = useState<string | null>(null);

  const { data: lead, isLoading: loadingLead } = useLead(open && editLeadId ? editLeadId : null);
  const {
    data: pipeline,
    isLoading: loadingPipeline,
    isError: pipelineError,
  } = useLeadPipeline(open);
  const {
    data: sources,
    isLoading: loadingSources,
    isError: sourcesError,
  } = useLeadSources(open);
  const { data: users = [], isLoading: loadingUsers } = useUserOptions();
  const { data: categoriesData, isLoading: loadingCategories } = useContactCategories();
  const { data: rolesData, isLoading: loadingRoles } = useContactRoles();
  const createContactMutation = useCreateContact();

  const ownerOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: u.label,
      })),
    [users]
  );
  const categoryOptions = useMemo(
    () =>
      (categoriesData ?? [])
        .filter((c) => c.isActive && c.id > 0)
        .map((c) => ({ value: c.id, label: c.label })),
    [categoriesData]
  );
  const roleOptions = rolesData ?? [];

  const initialValues = useMemo((): Partial<LeadFormValues> | null => {
    if (!lead) return null;
    return {
      title: lead.title,
      description: lead.description ?? undefined,
      contactId: lead.contactId,
      offerings: lead.offerings.map(o => ({ offeringId: o.offeringId, targetedAmount: o.targetedAmount })),
      assigneeUserId: lead.assigneeUserId,
      prospectingDate: lead.prospectingDate,
      estimatedClosingDate: lead.estimatedClosingDate ?? undefined,
      budgetScore: lead.budgetScore,
      authorityScore: lead.authorityScore,
      needScore: lead.needScore,
      timelineScore: lead.timelineScore,
      pipelineStageId: lead.pipelineStageId,
      sourceId: lead.sourceId,
      campaignId: lead.campaignId ?? undefined,
      referralContactId: lead.referralContactId ?? undefined,
      salesPersonId: lead.salesPersonId ?? undefined,
    };
  }, [lead]);

  const handleContactIdSet = useCallback(() => {
    setNewContactId(null);
  }, []);

  const handleContactSave = useCallback(
    (values: ContactFormValues) => {
      createContactMutation.mutate(values, {
        onSuccess: (data) => {
          setNewContactId(data.id);
          setQuickAddContactOpen(false);
        },
      });
    },
    [createContactMutation]
  );

  const isEdit = Boolean(editLeadId);

  return (
    <>
      <Drawer
        title={isEdit ? 'Edit Lead' : 'Add Lead'}
        open={open}
        onClose={onClose}
        width={720}
        destroyOnClose
        footer={null}
      >
        {isEdit && loadingLead ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <LeadForm
            mode={isEdit ? 'edit' : 'add'}
            editLeadId={editLeadId ?? undefined}
            initialValues={initialValues}
            editContactLabel={lead?.contactName}
            editReferralContactLabel={lead?.referralContactName}
            editSalesPersonLabel={lead?.salesPersonName}
            editAssigneeLabel={lead?.assigneeDisplayName}
            pipeline={pipeline}
            sources={sources}
            metadataLoading={loadingPipeline || loadingSources}
            metadataError={pipelineError || sourcesError}
            onSuccess={onSuccess}
            onCancel={onClose}
            newContactId={newContactId}
            onContactIdSet={handleContactIdSet}
            onQuickAddContact={!isEdit ? () => setQuickAddContactOpen(true) : undefined}
          />
        )}
      </Drawer>

      <ContactFormDrawer
        open={quickAddContactOpen}
        onClose={() => setQuickAddContactOpen(false)}
        mode="add"
        contactId={null}
        contact={null}
        loadingContact={false}
        ownerOptions={ownerOptions}
        categoryOptions={categoryOptions}
        roleOptions={roleOptions}
        loadingCategories={loadingCategories}
        loadingRoles={loadingRoles}
        loadingOwners={loadingUsers}
        onSave={handleContactSave}
        saving={createContactMutation.isPending}
      />
    </>
  );
};

export default LeadFormDrawer;
